import React from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { SUPPORTED_LANGUAGES } from '../i18n';
import { apiSetLanguage } from '../api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

/**
 * Compact language selector.
 * - variant="ghost" for use on marketing/login (no background)
 * - variant="default" for use inside chrome / cards
 * If a user is logged in, persists the choice to the backend.
 */
const LanguageSelector = ({ variant = 'default', className = '', showIcon = true, size = 'sm' }) => {
  const { i18n } = useTranslation();
  const { user, setUser } = useAuth();

  const handleChange = async (lng) => {
    await i18n.changeLanguage(lng);
    if (user) {
      try {
        await apiSetLanguage(lng);
        setUser?.((prev) => (prev ? { ...prev, language: lng } : prev));
        // Render toast in the NEW locale (t closure still references previous render)
        toast.success(i18n.t('language.changed', { lng }));
      } catch (e) {
        toast.error(i18n.t('language.saveError', { lng }));
      }
    }
  };

  const current = (i18n.language || 'it').split('-')[0];
  const heightCls = size === 'xs' ? 'h-7 text-xs' : size === 'sm' ? 'h-8 text-xs' : 'h-9 text-sm';
  const bgCls = variant === 'ghost'
    ? 'bg-transparent border-transparent hover:bg-white/10 text-current'
    : 'bg-white border-slate-200 text-slate-700';

  return (
    <Select value={current} onValueChange={handleChange}>
      <SelectTrigger
        data-testid="language-selector"
        className={`${heightCls} ${bgCls} gap-1.5 px-2 ${className}`}
        aria-label={i18n.t('language.menuLabel')}
      >
        {showIcon && <Globe className="w-3.5 h-3.5 opacity-70" />}
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="end">
        {SUPPORTED_LANGUAGES.map((code) => (
          <SelectItem key={code} value={code} data-testid={`lang-option-${code}`}>
            <span className="mr-1.5 font-mono uppercase text-[10px] text-slate-500">{code}</span>
            {i18n.t(`languages.${code}`)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default LanguageSelector;
